'use client';

import { useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { ArrowRight, Eye, EyeOff, Loader2 } from 'lucide-react';

import { AuthFormAlerts } from '@/components/auth/AuthFormAlerts';
import { BrandMark } from '@/components/BrandMark';
import { PasswordChecklist } from '@/components/PasswordChecklist';
import { api } from '@/lib/api';
import { type AuthFormAlert, parseAuthFormAlert } from '@/lib/auth-errors';
import { useStore } from '@/lib/store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PRIVACY_POLICY_VERSION } from '@/lib/legal-constants';
import {
  authCardClass,
  authChoiceCardClass,
  authMutedTextClass,
  authPageShellClass,
  authPanelClass,
} from '@/lib/auth-page-styles';

type Step = 'account' | 'verify';

export default function RegisterPage() {
  const router = useRouter();
  const setBootstrap = useStore((state) => state.setBootstrap);

  const [step, setStep] = useState<Step>('account');
  const [pendingEmail, setPendingEmail] = useState('');
  const [emailMasked, setEmailMasked] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    product_vertical: 'pvz' as 'pvz' | 'hospitality',
  });
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [alert, setAlert] = useState<AuthFormAlert | null>(null);
  const [emailDeliveryWarn, setEmailDeliveryWarn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const resendTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const startResendCooldown = () => {
    if (resendTimerRef.current) {
      clearInterval(resendTimerRef.current);
      resendTimerRef.current = null;
    }
    setResendCooldown(60);
    resendTimerRef.current = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) {
          if (resendTimerRef.current) {
            clearInterval(resendTimerRef.current);
            resendTimerRef.current = null;
          }
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const handleAccountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAlert(null);

    if (formData.password !== formData.confirmPassword) {
      setAlert({ message: 'Пароли не совпадают' });
      return;
    }

    if (formData.password.length < 10) {
      setAlert({ message: 'Пароль должен быть не короче 10 символов' });
      return;
    }
    if (formData.password.length > 128) {
      setAlert({ message: 'Пароль не должен быть длиннее 128 символов' });
      return;
    }
    if (!/[A-Za-z\u0400-\u04FF]/.test(formData.password)) {
      setAlert({ message: 'Добавьте в пароль хотя бы одну букву' });
      return;
    }
    if (!/\d/.test(formData.password)) {
      setAlert({ message: 'Добавьте в пароль хотя бы одну цифру' });
      return;
    }

    if (!acceptPrivacy) {
      setAlert({ message: 'Нужно согласие с политикой конфиденциальности' });
      return;
    }

    setLoading(true);

    try {
      const result = await api.register({
        email: formData.email,
        password: formData.password,
        product_vertical: formData.product_vertical,
        accept_privacy_policy: true,
        privacy_policy_version: PRIVACY_POLICY_VERSION,
      });
      if (result.verification_required) {
        setPendingEmail(result.email);
        setEmailMasked(result.email_masked || result.email);
        setEmailDeliveryWarn(Boolean(result.email_delivery_failed));
        setStep('verify');
        startResendCooldown();
        return;
      }
      if (result.bootstrap) {
        setBootstrap(result.bootstrap);
      }
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('auth_just_logged_in', '1');
      }
      router.replace('/setup?welcome=1');
    } catch (err: unknown) {
      setAlert(parseAuthFormAlert(err, 'Ошибка регистрации'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAlert(null);
    const trimmed = code.replace(/\s/g, '');
    if (trimmed.length < 4) {
      setAlert({ message: 'Введите код из письма' });
      return;
    }
    setLoading(true);
    try {
      const { bootstrap } = await api.verifyEmail({ email: pendingEmail, code: trimmed });
      if (bootstrap) {
        setBootstrap(bootstrap);
      }
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('auth_just_logged_in', '1');
      }
      router.replace('/setup?welcome=1');
    } catch (err: unknown) {
      setAlert(parseAuthFormAlert(err, 'Неверный или просроченный код'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!pendingEmail || resendCooldown > 0) return;
    setAlert(null);
    setLoading(true);
    try {
      await api.resendVerification(pendingEmail);
      startResendCooldown();
    } catch (err: unknown) {
      setAlert(parseAuthFormAlert(err, 'Не удалось отправить письмо'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={authPageShellClass}>
      <div className="mx-auto flex min-h-screen w-full max-w-5xl items-center justify-center">
        <Card className={`w-full max-w-md ${authCardClass}`}>
          <CardHeader className="space-y-4">
            <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--app-primary-soft)] ">
              <BrandMark size={40} />
            </div>
            <CardTitle className="text-center text-2xl font-bold">Умная смена</CardTitle>
            <CardDescription className={`text-center text-sm ${authMutedTextClass}`}>
              {step === 'account'
                ? '30 дней бесплатно после регистрации. Создайте аккаунт владельца — название организации и профиль заполните следующим шагом, уже внутри админки.'
                : `Мы отправили код на ${emailMasked}. Введите его ниже.`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 'account' ? (
              <form onSubmit={handleAccountSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="owner@company.ru"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Пароль</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Минимум 10 символов, буква и цифра"
                      value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      required
                      minLength={10}
                      maxLength={128}
                      disabled={loading}
                      autoComplete="new-password"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                      aria-pressed={showPassword}
                      className="absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
                      onClick={() => setShowPassword((v) => !v)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <PasswordChecklist value={formData.password} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Подтвердите пароль</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Повторите пароль"
                      value={formData.confirmPassword}
                      onChange={(e) => handleChange('confirmPassword', e.target.value)}
                      required
                      minLength={10}
                      maxLength={128}
                      disabled={loading}
                      autoComplete="new-password"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      aria-label={showConfirmPassword ? 'Скрыть подтверждение пароля' : 'Показать подтверждение пароля'}
                      aria-pressed={showConfirmPassword}
                      className="absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Тип организации</Label>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <label className={authChoiceCardClass(formData.product_vertical === 'pvz')}>
                      <input
                        type="radio"
                        name="product_vertical"
                        className="sr-only"
                        checked={formData.product_vertical === 'pvz'}
                        onChange={() => handleChange('product_vertical', 'pvz')}
                        disabled={loading}
                      />
                      <span className="font-medium">ПВЗ / ритейл</span>
                      <span className="text-xs text-muted-foreground">Пункты выдачи и смежные сценарии</span>
                    </label>
                    <label className={authChoiceCardClass(formData.product_vertical === 'hospitality')}>
                      <input
                        type="radio"
                        name="product_vertical"
                        className="sr-only"
                        checked={formData.product_vertical === 'hospitality'}
                        onChange={() => handleChange('product_vertical', 'hospitality')}
                        disabled={loading}
                      />
                      <span className="font-medium">Общепит</span>
                      <span className="text-xs text-muted-foreground">Кафе, рестораны — та же база, свои подписи и модули</span>
                    </label>
                  </div>
                </div>

                {alert ? <AuthFormAlerts {...alert} /> : null}

                <Button
                  type="submit"
                  className="h-11 w-full rounded-2xl text-sm font-semibold"
                  disabled={loading || !acceptPrivacy}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Создаём аккаунт...
                    </>
                  ) : (
                    <>
                      Зарегистрироваться
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>

                <label className={`flex cursor-pointer items-start gap-3 ${authPanelClass} text-sm text-[var(--app-text)]`}>
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 shrink-0 rounded border-[var(--app-border)]"
                    checked={acceptPrivacy}
                    onChange={(e) => setAcceptPrivacy(e.target.checked)}
                    disabled={loading}
                  />
                  <span>
                    Согласен(на) на{' '}
                    <Link href="/privacy" target="_blank" rel="noopener noreferrer" className="font-medium text-primary-600 underline underline-offset-2 hover:text-primary-700">
                      обработку персональных данных
                    </Link>{' '}
                    в соответствии с политикой конфиденциальности (версия {PRIVACY_POLICY_VERSION}).
                  </span>
                </label>

                <div className={`${authPanelClass} text-sm ${authMutedTextClass}`}>
                  На указанный email придёт код подтверждения. Без него войти в аккаунт не получится. Если начинали
                  регистрацию раньше и письма не было — введите тот же email и пароль ещё раз: мы обновим пароль и
                  отправим новый код.
                </div>

                <p className={`text-center text-sm ${authMutedTextClass}`}>
                  Уже есть аккаунт?{' '}
                  <Link href="/login" className="font-medium text-primary-600 hover:underline">
                    Войти
                  </Link>
                </p>
              </form>
            ) : (
              <form onSubmit={handleVerifySubmit} className="space-y-4">
                {emailDeliveryWarn && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-600 px-4 py-3 text-sm text-white dark:bg-amber-600">
                    Письмо могло не уйти с сервера. Проверьте папку «Спам», подождите пару минут или нажмите «Отправить код снова».
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="code">Код из письма</Label>
                  <Input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="Например 123456"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                    disabled={loading}
                    className="text-center text-lg tracking-widest"
                  />
                </div>

                {alert ? <AuthFormAlerts {...alert} /> : null}

                <Button
                  type="submit"
                  className="h-11 w-full rounded-2xl text-sm font-semibold"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Проверяем...
                    </>
                  ) : (
                    <>
                      Подтвердить и продолжить
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  className="h-11 w-full rounded-2xl text-sm"
                  disabled={loading || resendCooldown > 0}
                  onClick={handleResend}
                >
                  {resendCooldown > 0 ? `Отправить снова через ${resendCooldown} с` : 'Отправить код снова'}
                </Button>

                <button
                  type="button"
                  className={`w-full text-center text-sm ${authMutedTextClass} underline-offset-2 hover:underline`}
                  onClick={() => {
                    setStep('account');
                    setCode('');
                    setAlert(null);
                    setEmailDeliveryWarn(false);
                  }}
                >
                  Назад к email и паролю
                </button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
